<template>
    <svg
            v-if="puzzle"
            :viewBox="`0 0 ${viewBoxWidth} ${viewBoxHeight}`"
            class="w-full c-grid__svg"
            :style="disabled ? 'pointer-events: none;' : null"
            ref="svg"
            @touchmove="handleMouseMove"
    >
        <PuzzleCellValue v-for="item in cells"
            :key="item.id"
            :cell="item"
            :ref="`cell_${item.column}_${item.row}`"
            :isAdmin="isAdmin"
        />
    </svg>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import { sortBy, cloneDeep } from 'lodash'
  import gsap from "gsap"

  import PuzzleCellValue from '@/components/puzzle/PuzzleCellValue'

  export default {
    name: 'PuzzleGrid',

    props: ['isAdmin'],

    mounted () {
      if (!this.disabled) {
          document.addEventListener('mouseup', this.handleMouseUp)
          document.addEventListener('touchend', this.handleMouseUp)
      }
    },

    methods: {
      handleMouseUp () {
        this.$store.dispatch('finishCellSelection')
      },

      handleMouseMove (e) {
        if (!this.isSelectingAnswer) {
          return
        }

        const coordinatesObject = e.targetTouches ? e.targetTouches[0] : e
        const numberOfColumns = this.gridSettings.numberOfColumns
        const numberOfRows = this.gridSettings.numberOfRows

        const svgElement = this.$refs.svg
        const topLeftBounding = svgElement.querySelector('[data-column="0"][data-row="0"]').getBoundingClientRect()
        const bottomRightBounding = svgElement.querySelector(`[data-column="${numberOfColumns - 1}"][data-row="${numberOfRows - 1}"]`).getBoundingClientRect()

        const scrollLeft = window.pageXOffset
        const scrollTop = window.pageYOffset

        const gridPageX = topLeftBounding.left + scrollLeft
        const gridPageY = topLeftBounding.top + scrollTop

        const gridWidth = bottomRightBounding.right - topLeftBounding.left
        const gridHeight = bottomRightBounding.bottom - topLeftBounding.top

        const eventXPercentage = (coordinatesObject.pageX - gridPageX) / gridWidth
        const eventYPercentage = (coordinatesObject.pageY - gridPageY) / gridHeight

        let column = parseInt(numberOfColumns * eventXPercentage)
        column = Math.abs(column)
        column = Math.min(column, numberOfColumns)

        let row = parseInt(numberOfRows * eventYPercentage)
        row = Math.abs(row)
        row = Math.min(row, numberOfRows)

        const cellComponents = this.$refs[`cell_${column}_${row}`]

        // if (!cellComponents) {
        //   console.log('no component for these coordinates', { column, row })
        //   return
        // }

        cellComponents[0].selectCell()
      },

      animateFoundWord (word_id) {
        let foundCells = [...this.cells.filter(item => item.word_id === word_id)]
        foundCells = sortBy(foundCells, ['index', 'row', 'column'])

        let cellTexts = []
        let cellRects = []
        foundCells.forEach((cell) => {
          const node = this.$el.querySelector('.c-grid__cell[data-index="' + cell.id + '"]')
          cellTexts.push(node.lastElementChild)
          cellRects.push(node.firstElementChild)
        })

        const tl = gsap.timeline({
          onComplete: () => {
            this.$store.commit('setFoundWordIdToAnimate', { word_id: null })
            }
        });

        tl
          .to(cellRects, {
            fill: '#DDDDDD',
            duration: .9,
            stagger: {
              amount: .2,
            },
            ease: "power1.inOut"
          }, 0)
          .to(cellTexts, {
            fill: '#0AA630',
            duration: 1,
            stagger: {
              amount: .2,
            },
            ease: "power1.inOut"
          }, '>-1')
          .to(cellTexts, {
            duration: 0.3,
            transformOrigin: 'center center',
            scale: .6,
            stagger: {
              amount: .2,
            },
            ease: "power1.inOut"
          }, '>-.9')
          .to(cellTexts, {
            duration: 0.3,
            transformOrigin: 'center center',
            scale: 1.4,
            stagger: {
              amount: .2,
            },
            ease: "power1.inOut"
          }, '>-.2')
          .to(cellTexts, {
            duration: 0.3,
            scale: 1,
            fill: '#000',
            stagger: {
              amount: .2,
            },
            ease: "power1.inOut"
          }, '>-.1')
      }
    },

    watch: {
      foundWordIdToAnimate: function (newValue) {
        if (newValue === null) {
          return
        }

        this.$nextTick(() => {
          this.animateFoundWord(newValue)
        })
      }
    },

    computed: {
      ...mapGetters([
        'allNonBonusWordsFound'
      ]),

      ...mapState({
        puzzle: state => state.currentPuzzle,
        gridSettings: state => state.gridSettings,
        isSelectingAnswer: state => state.gridSettings.isSelectingAnswer,
        activeWordIndex: state => state.activeWordIndex,
        foundWordIds: state => state.foundWordIds,
        foundWordIdToAnimate: state => state.foundWordIdToAnimate
      }),

      viewBoxWidth () {
        return this.gridSettings.cellSize * this.gridSettings.numberOfColumns + 2
      },

      viewBoxHeight () {
        return this.gridSettings.cellSize * this.gridSettings.numberOfRows + 2
      },

      cellsMapped () {
        let cells = []

        this.puzzle.words.forEach((word, index) => {
          let wordCells = [...word.cells]

          if (this.isAdmin) {
            const activeWordIndex = this.activeWordIndex
            let color = word.color
            if (activeWordIndex !== null && activeWordIndex !== index) {
              color = null
            }

            wordCells = cloneDeep(wordCells).map((item, cellIndex) => {
              if (activeWordIndex === index && word.word.length - 1 < cellIndex) {
                color = '#DDDDDD'
              }

              const letter = word.is_bonus ? 'X' : this.$options.filters.numberToLetter(index)
              item.color = color
              item.placeholder = `${letter}${cellIndex}`
              return item
            })
          }

          cells = [...cells, ...wordCells]
        })

        return cells
      },

      cells () {
        const cells = this.cellsMapped

        // Sort cells so bonus word cells are last in the svg html: needed for rendering cell borders correctly
        if (this.allNonBonusWordsFound) {
          return sortBy(cells, ['word_id', 'row' , 'column'])
        }

        return sortBy(cells, ['row' , 'column'])
      },

      disabled () {
        return this.isAdmin
      },

      words () {
        return this.puzzle.words.filter(item => !item.is_bonus)
      }
    },

    components: {
      PuzzleCellValue
    }
  }
</script>

